import { z } from "zod";

import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { JsonTable } from "~/components/json-table";
import type { Locale } from "~/i18n/resolveLocale";
import { safeHttpUrl } from "~/utils/safeUrl";

const zSelectOption = z.object({
  value: z.string(),
  label: z.object({ en: z.string().optional(), ja: z.string().optional() }).optional(),
});
const zSelectOptions = z.array(zSelectOption);
type SelectOption = z.infer<typeof zSelectOption>;

const zMediaItem = z.object({
  url: z.string(),
  mimeType: z.string(),
  attribution: z.object({ text: z.string().optional(), url: z.string().optional() }).optional(),
});
const zMediaItems = z.array(zMediaItem);

const resolveSelectLabel = (rawValue: unknown, options: SelectOption[], locale: Locale): string => {
  const value = typeof rawValue === "string" ? rawValue : String(rawValue);
  const option = options.find((o) => o.value === value);
  if (!option) return value;
  const label = option.label?.[locale] ?? option.label?.en ?? option.label?.ja;
  return label && label.length > 0 ? label : value;
};

interface EntityPropertyValueProps {
  value: unknown;
  propertyTypeId: string;
  selectOptions: unknown;
}

/** Renders a single property value by its schema type (V1 PropertyValue parity). */
export function EntityPropertyValue({ value, propertyTypeId, selectOptions }: EntityPropertyValueProps) {
  const locale = useDisplayLocale();
  const options = zSelectOptions.safeParse(selectOptions);

  if (value === null || value === undefined || value === "") return <span className="text-fg-3">—</span>;

  switch (propertyTypeId) {
    case "media": {
      const media = zMediaItems.safeParse(value);
      if (!media.success) return <JsonTable target={value} />;
      return (
        <div className="space-y-2">
          {media.data.map((item, i) => {
            // Gate stored URLs to http(s) — never render a javascript:/data: scheme as src/href.
            const href = safeHttpUrl(item.url);
            return (
              <figure key={i} className="space-y-1">
                {href && item.mimeType.startsWith("image/") ? (
                  <img src={href} alt={item.attribution?.text ?? item.url} className="max-w-[200px] rounded" />
                ) : href ? (
                  <a href={href} target="_blank" rel="noreferrer" className="text-fg-link break-all hover:underline">
                    {item.url}
                  </a>
                ) : (
                  <span className="break-all text-fg-3">{item.url}</span>
                )}
                {item.attribution?.text && (
                  <figcaption className="text-[11px] text-fg-3">{item.attribution.text}</figcaption>
                )}
              </figure>
            );
          })}
        </div>
      );
    }

    case "select": {
      if (!options.success || options.data.length === 0) return <JsonTable target={value} />;
      return <span className="whitespace-nowrap">{resolveSelectLabel(value, options.data, locale)}</span>;
    }

    case "multiSelect": {
      if (!Array.isArray(value) || !options.success || options.data.length === 0) return <JsonTable target={value} />;
      return (
        <span className="whitespace-nowrap">
          {value.map((v) => resolveSelectLabel(v, options.data, locale)).join(", ")}
        </span>
      );
    }

    default: {
      if (typeof value === "object") return <JsonTable target={value} />;
      if (typeof value === "string") return <span className="whitespace-pre-line break-words">{value}</span>;
      return <span className="whitespace-nowrap tabular-nums">{String(value)}</span>;
    }
  }
}
